import { useEffect, useState } from 'react'
import Modal from '../Modal'
import InputField from '../InputField'
import Select from '../Select'
import { PayrollCodeConfigurationModel, PayrollCodeType, PayrollCodeValueSource } from '../../redux/types'
import Switch from '../Switch'

interface Props {
    onAddClick?: (payrollCode: PayrollCodeConfigurationModel) => void
    onCancelClick?: () => void
    visible: boolean
}

const defaultPayrollCode : PayrollCodeConfigurationModel = {
    id: null,
    type: PayrollCodeType.advance,
    code: '',
    valueSource: PayrollCodeValueSource.grossAmount,
    value: null,
    negate: false,
    includeIdentificationNumber: false,
    includeName: false,
    includeFromDate: false,
    includeToDate: false
    }

const AddPayrollCodeModal = ({ onAddClick, onCancelClick, visible }: Props) => {
    const [payrollCode, setPayrollCode] = useState<PayrollCodeConfigurationModel>(defaultPayrollCode)

    useEffect(() => {
        if (visible) {
            setPayrollCode({...defaultPayrollCode})
        }
    }, [visible])

    const handleOnAdd = () => {
        if (payrollCode)
            onAddClick && onAddClick(payrollCode)
    }

    const handleOnClose = () => {
        setPayrollCode({...defaultPayrollCode})
        onCancelClick && onCancelClick()
    }

    const handleOnChange = (propName: string, value: any) => {
        setPayrollCode({...payrollCode, [propName]: value})
    }

    const getPayrollCodeTypeOptions = () => {
        return Object.keys(PayrollCodeType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getValueSourceOptions = () => {
        return Object.keys(PayrollCodeValueSource).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    return (
        <Modal
            title='Add payroll code'
            show={visible}
            okButtonTitle='Save'
            cancelButtonTitle='Cancel'
            onHide={() => handleOnClose()}
            onOkClick={() => handleOnAdd()}
            onCancelClick={() => handleOnClose()}
        >
            <Select
                id='type'
                key='type'
                label='Type'
                options={getPayrollCodeTypeOptions()}
                selectedValue={payrollCode.type.toString()}
                onSelect={(value) => handleOnChange('type', value)}
            />
            <InputField
                id='code'
                value={payrollCode.code}
                onChange={(event) => handleOnChange('code', event.target.value)}
                label='Code'
                placeholder=''
            />
            <Select
                id='valueSource'
                key='valueSource'
                label='Value source'
                options={getValueSourceOptions()}
                selectedValue={payrollCode.valueSource.toString()}
                onSelect={(value) => handleOnChange('valueSource', value)}
            />
            <InputField
                id='value'
                disabled={payrollCode.valueSource !== PayrollCodeValueSource.value}
                value={payrollCode.value ?? ''}
                onChange={(event) => handleOnChange('value', event.target.value)}
                label='Value'
                placeholder=''
              />
            <Switch
                id='negate'
                key='negate'
                disabled={payrollCode.valueSource === PayrollCodeValueSource.value}
                checked={payrollCode.negate ?? false}
                label='Negate value source value'
                onChange={() => handleOnChange('negate', !(payrollCode.negate ?? false))}
            />
            <Switch
                id='includeIdentificationNumber'
                key='includeIdentificationNumber'
                checked={payrollCode.includeIdentificationNumber ?? false}
                label='Include identification number'
                onChange={() => handleOnChange('includeIdentificationNumber', !(payrollCode.includeIdentificationNumber ?? false))}
            />
            <Switch
                id='includeName'
                key='includeName'
                checked={payrollCode.includeName ?? false}
                label='Include name'
                onChange={() => handleOnChange('includeName', !(payrollCode.includeName ?? false))}
            />
            <Switch
                id='includeFromDate'
                key='includeFromDate'
                checked={payrollCode.includeFromDate ?? false}
                label='Include from date'
                onChange={() => handleOnChange('includeFromDate', !(payrollCode.includeFromDate ?? false))}
            />
            <Switch
                id='includeToDate'
                key='includeToDate'
                checked={payrollCode.includeToDate ?? false}
                label='Include to date'
                onChange={() => handleOnChange('includeToDate', !(payrollCode.includeToDate ?? false))}
            />
        </Modal>
    )
}

export default AddPayrollCodeModal
